/**
 * AIProcessingLoader Component
 * Animated loader for AI operations with progress
 */

import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { 
  Brain, 
  Sparkles, 
  FileSearch, 
  Loader2, 
  CheckCircle2,
  Cpu,
  Wand2,
} from 'lucide-react';
import { Progress } from '@/components/ui/progress';

/**
 * Processing step configuration
 */
const PROCESSING_STEPS = {
  analyzing: { label: 'Analyzing document...', icon: FileSearch },
  extracting: { label: 'Extracting content...', icon: Brain },
  generating: { label: 'Generating text...', icon: Wand2 },
  processing: { label: 'Processing...', icon: Cpu },
  finalizing: { label: 'Finalizing...', icon: Sparkles },
  complete: { label: 'Complete!', icon: CheckCircle2 },
};

/**
 * Animated dots component
 */
function AnimatedDots() {
  return (
    <span className="inline-flex">
      <span className="animate-bounce [animation-delay:-0.3s]">.</span>
      <span className="animate-bounce [animation-delay:-0.15s]">.</span>
      <span className="animate-bounce">.</span>
    </span>
  );
}

/**
 * Pulsing brain animation
 */
function PulsingBrain({ className }) {
  return (
    <div className={cn('relative', className)}>
      <div className="absolute inset-0 bg-primary/20 rounded-full animate-ping" />
      <div className="relative flex items-center justify-center w-16 h-16 rounded-full bg-primary/10">
        <Brain className="h-8 w-8 text-primary animate-pulse" />
      </div>
    </div>
  );
}

/**
 * Spinning loader with sparkles
 */
function SparkleLoader({ className }) {
  return (
    <div className={cn('relative', className)}>
      <Loader2 className="h-12 w-12 text-primary animate-spin" />
      <Sparkles className="absolute -top-1 -right-1 h-5 w-5 text-amber-500 animate-pulse" />
      <Sparkles className="absolute -bottom-1 -left-1 h-4 w-4 text-amber-500 animate-pulse [animation-delay:0.5s]" />
    </div>
  );
}

/**
 * Simple inline AI loader
 */
export function AIInlineLoader({ text = 'Processing', className }) {
  return (
    <span className={cn('inline-flex items-center gap-2 text-muted-foreground', className)} data-testid="ai-inline-loader">
      <Loader2 className="h-4 w-4 animate-spin" />
      <span className="text-sm">
        {text}
        <AnimatedDots />
      </span>
    </span>
  );
}

/**
 * AI Processing card with progress
 */
export function AIProcessingCard({
  title = 'AI Processing',
  description,
  progress = 0,
  step = 'processing',
  showProgress = true,
  className,
}) {
  const stepConfig = PROCESSING_STEPS[step] || PROCESSING_STEPS.processing;
  const StepIcon = stepConfig.icon;
  const isComplete = step === 'complete';

  return (
    <div 
      className={cn(
        'p-6 rounded-lg border bg-card text-center',
        className
      )}
      data-testid="ai-processing-card"
    >
      <div className="flex flex-col items-center gap-4">
        {/* Animated icon */}
        <div className={cn(
          'w-16 h-16 rounded-full flex items-center justify-center',
          isComplete ? 'bg-green-100 dark:bg-green-900/30' : 'bg-primary/10'
        )}>
          {isComplete ? (
            <CheckCircle2 className="h-8 w-8 text-green-500" />
          ) : (
            <StepIcon className="h-8 w-8 text-primary animate-pulse" />
          )}
        </div>

        {/* Title and description */}
        <div>
          <h3 className="font-semibold text-lg">{title}</h3>
          <p className="text-sm text-muted-foreground mt-1">
            {description || stepConfig.label}
            {!isComplete && <AnimatedDots />}
          </p>
        </div>

        {/* Progress bar */}
        {showProgress && !isComplete && (
          <div className="w-full max-w-xs">
            <Progress value={progress} className="h-2" />
            <p className="text-xs text-muted-foreground mt-1">{Math.round(progress)}%</p>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Full page AI processing overlay
 */
export function AIProcessingOverlay({
  isVisible = false,
  title = 'AI Processing',
  description = 'Please wait while we process your request',
  progress = 0,
  step = 'processing',
  onCancel,
  className,
}) {
  const stepConfig = PROCESSING_STEPS[step] || PROCESSING_STEPS.processing;
  const isComplete = step === 'complete';

  if (!isVisible) return null;

  return (
    <div 
      className={cn(
        'fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm',
        className
      )}
      data-testid="ai-processing-overlay"
    >
      <div className="bg-card border rounded-xl shadow-xl p-8 max-w-md w-full mx-4">
        <div className="flex flex-col items-center gap-6">
          {/* Animated loader */}
          {isComplete ? (
            <div className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <CheckCircle2 className="h-10 w-10 text-green-500" />
            </div>
          ) : (
            <SparkleLoader />
          )}

          {/* Text */}
          <div className="text-center">
            <h2 className="text-xl font-semibold">{title}</h2>
            <p className="text-muted-foreground mt-2">
              {isComplete ? 'Processing complete!' : description}
            </p>
          </div>

          {/* Step indicator */}
          {!isComplete && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <stepConfig.icon className="h-4 w-4" />
              <span>{stepConfig.label}</span>
            </div>
          )}

          {/* Progress */}
          {!isComplete && (
            <div className="w-full">
              <Progress value={progress} className="h-2" />
              <p className="text-center text-sm text-muted-foreground mt-2">
                {Math.round(progress)}% complete
              </p>
            </div>
          )}

          {/* Cancel button */}
          {onCancel && !isComplete && (
            <button
              onClick={onCancel}
              className="text-sm text-muted-foreground hover:text-foreground underline"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Multi-step AI processing indicator
 */
export function AIProcessingSteps({
  steps = [],
  currentStep = 0,
  className,
}) {
  return (
    <div className={cn('space-y-3', className)} data-testid="ai-processing-steps">
      {steps.map((step, index) => {
        const isActive = index === currentStep;
        const isComplete = index < currentStep;
        const isPending = index > currentStep;

        return (
          <div
            key={step.id || index}
            className={cn(
              'flex items-center gap-3 p-3 rounded-lg transition-colors',
              isActive && 'bg-primary/10',
              isComplete && 'opacity-60'
            )}
          >
            <div className={cn(
              'w-8 h-8 rounded-full flex items-center justify-center',
              isComplete && 'bg-green-100 dark:bg-green-900/30',
              isActive && 'bg-primary/20',
              isPending && 'bg-muted'
            )}>
              {isComplete ? (
                <CheckCircle2 className="h-4 w-4 text-green-500" />
              ) : isActive ? (
                <Loader2 className="h-4 w-4 text-primary animate-spin" />
              ) : (
                <span className="text-xs text-muted-foreground">{index + 1}</span>
              )}
            </div>
            
            <div className="flex-1">
              <p className={cn(
                'text-sm font-medium',
                isPending && 'text-muted-foreground'
              )}>
                {step.label}
              </p>
              {step.description && (
                <p className="text-xs text-muted-foreground">{step.description}</p>
              )}
            </div>

            {isActive && (
              <span className="text-xs text-primary">
                In progress<AnimatedDots />
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Main AIProcessingLoader component
 */
export function AIProcessingLoader({
  variant = 'card',
  isVisible = true,
  title,
  description,
  progress = 0,
  step = 'processing',
  steps = [],
  currentStep = 0,
  onCancel,
  className,
}) {
  if (!isVisible) return null;

  switch (variant) {
    case 'inline':
      return <AIInlineLoader text={title} className={className} />;
    case 'overlay':
      return (
        <AIProcessingOverlay
          isVisible={isVisible}
          title={title}
          description={description}
          progress={progress}
          step={step}
          onCancel={onCancel}
          className={className}
        />
      );
    case 'steps':
      return (
        <AIProcessingSteps
          steps={steps}
          currentStep={currentStep}
          className={className}
        />
      );
    case 'card':
    default:
      return (
        <AIProcessingCard
          title={title}
          description={description}
          progress={progress}
          step={step}
          className={className}
        />
      );
  }
}

export default AIProcessingLoader;
